// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { Client } from '@microsoft/microsoft-graph-client';

import { OAuthSettings } from '../oauth/microsoft.oauth';
import { MicrosoftUser } from '../oauth/microsoft.user';
import { AlertsService } from './alert.service';


@Injectable({
  providedIn: 'root'
})

export class MicrosoftAuthService {
  public authenticated: boolean;
  public user: MicrosoftUser;

  // <constructorSnippet>
  constructor (
    private msalService: MsalService,
    private alertsService: AlertsService) {

    this.authenticated = false;
    this.user = undefined;
  }
  // </constructorSnippet>

  // Prompt the user to sign in and
  // grant consent to the requested permission scopes
  async signIn(): Promise<void> {
    const result = await firstValueFrom(this.msalService
      .loginPopup(OAuthSettings))
      .catch((reason) => {
        this.alertsService.add('Login failed', JSON.stringify(reason, null, 2));
      });

    if (result) {
      this.msalService.instance.setActiveAccount(result.account);
      this.authenticated = true;
      this.user = await this.getUser();
    }
  }

  // Sign out
  signOut(): void {
    this.msalService.logout();
    this.user = null;
    this.authenticated = false;
  }

  // Silently request an access token
  async getAccessToken(): Promise<string> {
    const result = await firstValueFrom(this.msalService.acquireTokenSilent(OAuthSettings))
      .catch((reason) => {
        this.alertsService.add('Get token failed', JSON.stringify(reason, null, 2));
      });

    if (result) {
      return result.accessToken;
    }
    return null;
  }

  // <getUserSnippet>
  private async getUser(): Promise<MicrosoftUser> {
    if (!this.authenticated) { return null; }

    const graphClient = Client.init({
      // Initialize the Graph client with an auth
      // provider that requests the token from the
      // auth service
      authProvider: async (done) => {
        const token = await this.getAccessToken()
          .catch((reason) => {
            done(reason, null);
          });

        if (token) {
          done(null, token);
        } else {
          done('Could not get an access token', null);
        }
      }
    });

    // Get the user from Graph (GET /me)
    const graphUser = await graphClient
      .api('/me')
      .select('displayName,mail,mailboxSettings,userPrincipalName')
      .get();

    const user = new MicrosoftUser();
    user.displayName = graphUser.displayName;
    user.timeZone = graphUser.mailboxSettings.timeZone;
    // Prefer the mail property, but fall back to userPrincipalName
    user.email = graphUser.mail || graphUser.userPrincipalName;

    return user;
  }
  // </getUserSnippet>
}
